import React from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

const ContactSection = ({ 
  title, 
  description,
  contactInfo = {},
  customClass = '',
  backgroundColor = 'bg-gray-50' 
}) => {
  return (
    <section className={`py-16 px-4 ${backgroundColor} ${customClass}`}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left column - Title and description */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {parse(title)}
            </h2>
            {description && (
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {parse(description)}
              </p>
            )}
            
            {/* Action buttons */}
            <div className="flex gap-4 flex-wrap">
              <a href={`sms:${contactInfo.phone}`} className="bg-gray-800 text-white px-6 py-3 rounded-md font-medium hover:bg-gray-900 transition-colors inline-block text-center">
                Text us
              </a>

              <a href={`tel:${contactInfo.phone}`} className="border border-gray-300 text-gray-700 px-6 py-3 rounded-md font-medium hover:bg-gray-50 transition-colors inline-block text-center">
                Call us
              </a>

            </div>
          </div>
          
          {/* Right column - Contact card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="border-b pb-2 text-lg font-semibold text-gray-900 mb-6">Get in touch</h3>
            
            <div className="space-y-4">
              {contactInfo.phone && (
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <a 
                    href={`tel:${contactInfo.phone}`}
                    className="text-gray-700 hover:text-gray-900"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
              )}
              
              {contactInfo.email && (
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className="text-gray-700 hover:text-gray-900"
                  >
                    {contactInfo.email}
                  </a>
                </div>
              )}
              
              {contactInfo.hours && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-gray-700">
                    {Array.isArray(contactInfo.hours) ? (
                      contactInfo.hours.map((hour, index) => (
                        <p key={index} className="text-sm">{hour}</p>
                      ))
                    ) : (
                      <p className="text-sm">{contactInfo.hours}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

ContactSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  contactInfo: PropTypes.shape({
    phone: PropTypes.string,
    email: PropTypes.string,
    hours: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ])
  }),
  customClass: PropTypes.string,
  backgroundColor: PropTypes.string
};

export default ContactSection;
